import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtUSD, fmtPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const META = {
  "EUR/USD": { multiplier: 100000, decimals: 5, jpy: false, xau: false },
  "GBP/USD": { multiplier: 100000, decimals: 5, jpy: false, xau: false },
  "AUD/USD": { multiplier: 100000, decimals: 5, jpy: false, xau: false },
  "NZD/USD": { multiplier: 100000, decimals: 5, jpy: false, xau: false },
  "USD/JPY": { multiplier: 100000, decimals: 3, jpy: true,  xau: false },
  "USD/CAD": { multiplier: 100000, decimals: 5, jpy: true,  xau: false },
  "USD/CHF": { multiplier: 100000, decimals: 5, jpy: true,  xau: false },
  "XAU/USD": { multiplier: 100,    decimals: 2, jpy: false, xau: true  },
};

function previewPnL(trade, closePrice) {
  if (!closePrice || isNaN(closePrice)) return 0;
  const m = META[trade.instrument];
  if (!m) return 0;
  const dir = trade.side === "buy" ? 1 : -1;
  const diff = (closePrice - trade.entry_price) * dir;
  if (m.xau) return diff * trade.lots * m.multiplier;
  // USD-base pairs (USD/JPY etc.) → convert quote to USD at close price
  if (m.jpy) return (diff * trade.lots * m.multiplier) / closePrice;
  return diff * trade.lots * m.multiplier;
}

// Inverse: given desired P&L, find required close price.
function priceForPnL(trade, desiredPnL) {
  const m = META[trade.instrument];
  if (!m || !trade.lots) return null;
  const dir = trade.side === "buy" ? 1 : -1;
  if (m.jpy) {
    // pnl = (price - entry) * dir * lots * 100000 / price
    //  ⇒ price * (pnl - dir*lots*100000) = -entry * dir * lots * 100000
    //  ⇒ price = (entry * dir * lots * 100000) / (dir*lots*100000 - pnl)
    const N = trade.entry_price * dir * trade.lots * m.multiplier;
    const D = dir * trade.lots * m.multiplier - desiredPnL;
    if (D === 0) return null;
    return N / D;
  }
  // Linear: pnl = (price - entry) * dir * lots * mult
  return trade.entry_price + desiredPnL / (dir * trade.lots * m.multiplier);
}

export default function CloseTradeDialog({ trade, open, onOpenChange, onClosed }) {
  const [marketPrice, setMarketPrice] = useState(null);
  const [priceStr, setPriceStr] = useState("");
  const [pnlStr, setPnlStr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const m = trade ? META[trade.instrument] : null;

  // Poll live market price
  useEffect(() => {
    if (!trade || !open) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/market/rates/${encodeURIComponent(trade.instrument)}`);
        if (!mounted) return;
        setMarketPrice(data.mid);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 2000);
    return () => { mounted = false; clearInterval(id); };
  }, [trade, open]);

  // Initialize fields when dialog opens
  useEffect(() => {
    if (open && trade && marketPrice && !priceStr) {
      const p = marketPrice.toFixed(m.decimals);
      setPriceStr(p);
      setPnlStr(previewPnL(trade, parseFloat(p)).toFixed(2));
    }
    if (!open) {
      setPriceStr("");
      setPnlStr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, marketPrice]);

  const computedPnL = useMemo(() => {
    if (!trade) return 0;
    return previewPnL(trade, parseFloat(priceStr));
  }, [trade, priceStr]);

  const handlePriceChange = (v) => {
    setPriceStr(v);
    const f = parseFloat(v);
    if (!isNaN(f) && trade) setPnlStr(previewPnL(trade, f).toFixed(2));
  };
  const handlePnLChange = (v) => {
    setPnlStr(v);
    const f = parseFloat(v);
    if (!isNaN(f) && trade) {
      const p = priceForPnL(trade, f);
      if (p !== null && isFinite(p)) setPriceStr(p.toFixed(m.decimals));
    }
  };
  const useMarket = () => {
    if (!marketPrice) return;
    handlePriceChange(marketPrice.toFixed(m.decimals));
  };

  const submit = async () => {
    const close_price = parseFloat(priceStr);
    if (isNaN(close_price) || close_price <= 0) {
      toast.error("Enter a valid close price");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/trades/${trade.id}/close`, { close_price });
      toast.success(`Trade closed at ${close_price.toFixed(m.decimals)} (P&L ${fmtUSD(computedPnL, { signed: true })})`);
      onOpenChange(false);
      onClosed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close trade");
    } finally {
      setSubmitting(false);
    }
  };

  if (!trade) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-subtle max-w-md" data-testid="close-trade-dialog">
        <DialogHeader>
          <DialogTitle>Close Trade</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Set the close price to book profit or loss as desired, or use the live market price.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-px bg-border">
            <div className="bg-background p-3"><div className="label-eyebrow mb-1">Instrument</div><div className="font-semibold">{trade.instrument}</div></div>
            <div className="bg-background p-3"><div className="label-eyebrow mb-1">Side / Lots</div><div className="font-mono-num"><span className={trade.side === "buy" ? "text-profit" : "text-loss"}>{trade.side.toUpperCase()}</span> · {trade.lots.toFixed(2)}</div></div>
            <div className="bg-background p-3"><div className="label-eyebrow mb-1">Entry Price</div><div className="font-mono-num font-semibold">{fmtPrice(trade.instrument, trade.entry_price)}</div></div>
            <div className="bg-background p-3"><div className="label-eyebrow mb-1">Market (live)</div><div className="font-mono-num font-semibold">{marketPrice ? fmtPrice(trade.instrument, marketPrice) : "…"}</div></div>
          </div>

          <div>
            <Label className="label-eyebrow">Close Price</Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="number"
                step="0.00001"
                value={priceStr}
                onChange={(e) => handlePriceChange(e.target.value)}
                className="bg-background border-subtle font-mono-num h-11"
                data-testid="close-price-input"
              />
              <Button type="button" variant="outline" onClick={useMarket} className="h-11 shrink-0" data-testid="close-use-market">
                Use Market
              </Button>
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Desired P&L (USD)</Label>
            <Input
              type="number"
              step="0.01"
              value={pnlStr}
              onChange={(e) => handlePnLChange(e.target.value)}
              className="mt-2 bg-background border-subtle font-mono-num h-11"
              data-testid="close-pnl-input"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Enter a target profit or loss — the close price auto-adjusts.
            </p>
          </div>

          <div className="border border-subtle p-4 bg-background">
            <div className="label-eyebrow mb-2">Booking Preview</div>
            <div className="flex items-end justify-between">
              <div className="text-sm text-muted-foreground">P&L credited to client balance</div>
              <div
                className={`font-mono-num text-2xl font-semibold ${computedPnL >= 0 ? "text-profit" : "text-loss"}`}
                data-testid="close-pnl-preview"
              >
                {fmtUSD(computedPnL, { signed: true })}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={computedPnL >= 0 ? "bg-profit hover:bg-profit/90 text-white" : "bg-loss hover:bg-loss/90 text-white"}
            data-testid="close-confirm"
          >
            {submitting ? "Booking…" : `Book ${computedPnL >= 0 ? "Profit" : "Loss"} ${fmtUSD(computedPnL, { signed: true })}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
